import axios from 'axios';

export interface SearchGuestsParams {
  restaurantId: string;
  search: string;
  page?: number;
  perPage?: number;
  sort?: 'name' | 'email' | 'phone';
  uid?: string;
  version?: 'v01' | 'v02';
}

export interface GuestSearchResult {
  id: string;
  name: string;
  email: string;
  phone: string;
  attr: string[];
  lastReservation?: string;
  visits?: number;
  vip?: boolean;
  regular?: boolean;
  blacklist?: boolean;
}

// v01: NO guestbook app — uses the existing Elasticsearch-backed endpoint.
const searchGuestsV01 = async ({
  restaurantId,
  search,
  page = 0,
  perPage = 10,
  sort = 'name',
  uid,
}: SearchGuestsParams): Promise<GuestSearchResult[]> => {
  if (!search || search.length < 3) {
    return [];
  }

  try {
    const url = `https://api.gastronaut.ai/v03/guests/searchV02/${restaurantId}/${page}/${perPage}/${
      search?.startsWith('0') ? search.slice(1) : search
    }?sort=${sort}`;

    const { data } = await axios.get<{
      results: {
        id: string;
        restaurant: string;
        name: string;
        email: string;
        phone: string;
        visits: number;
        vip: boolean;
        regular: boolean;
        blacklist: boolean;
        last_visit_date: string;
      }[];
    }>(url, {
      headers: { uid },
    });

    const { results } = data;

    return results.map((r) => ({
      id: r.id,
      name: r.name || '',
      email: r.email || '',
      phone: r.phone || '',
      attr: [
        r.vip ? 'vip' : '',
        r.blacklist ? 'blacklist' : '',
        r.regular ? 'regular' : '',
      ].filter(Boolean),
      lastReservation: r.last_visit_date || '',
      visits: r.visits || 0,
      vip: r.vip || false,
      regular: r.regular || false,
      blacklist: r.blacklist || false,
    }));
  } catch (error) {
    console.error('❌ API search error (v01):', error);
    throw error;
  }
};

// v02: HAS guestbook app — proxies through gastronaut-api so the browser
// never holds a Supabase key. The server queries GuestsV02 with the secret
// key (RLS-locked), authorizes via Firebase uid + hasUserRights().
const searchGuestsV02 = async ({
  restaurantId,
  search,
  page = 0,
  perPage = 10,
  sort = 'name',
  uid,
}: SearchGuestsParams): Promise<GuestSearchResult[]> => {
  if (!search || search.length < 3) {
    return [];
  }

  try {
    const { data } = await axios.post<{ results: GuestSearchResult[] }>(
      `https://api.gastronaut.ai/v03/guests/searchSupabase/${restaurantId}`,
      { search, page, perPage, sort },
      { headers: { uid } },
    );

    return data.results;
  } catch (error) {
    console.error('❌ searchGuests V02 error:', error);
    throw error;
  }
};

/**
 * Main routing function for guest search
 *
 * Decision Logic:
 * - v01 (NO guestbook app): Uses API endpoint api.gastronaut.ai/v03/guests/searchV02/
 * - v02 (HAS guestbook app): Proxies through api.gastronaut.ai/v03/guests/searchSupabase/
 *
 * Version is determined by checking if 'guestbook' exists in restaurant.apps array
 */
const searchGuests = async ({
  restaurantId,
  search,
  page = 0,
  perPage = 10,
  sort = 'name',
  uid,
  version,
}: SearchGuestsParams): Promise<GuestSearchResult[]> => {
  if (version !== 'v02') {
    return searchGuestsV01({
      restaurantId,
      search,
      page,
      perPage,
      sort,
      uid,
      version,
    });
  } else {
    return searchGuestsV02({
      restaurantId,
      search,
      page,
      perPage,
      sort,
      uid,
      version,
    });
  }
};

export default searchGuests;
